/**
 * MentionTextarea — Ant Design themed textarea with @mention autocomplete.
 *
 * When the user types `@`, a dropdown appears with matching users and contacts.
 * Selecting an entry inserts `@[Display Name](wsid)` into the text.
 * The coreBOS workflow triggers email on the @mention pattern.
 *
 * Adapted from components/digibox/MentionTextarea.tsx for the main CRM frontend.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { Input, Tag, theme } from "antd";
import axios from "axios";

interface Mentionable {
  id: string;
  name: string;
  type: "user" | "contact";
  email?: string;
}

interface MentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  onSubmit?: () => void;
  disabled?: boolean;
}

/** Cache mentionables so we only fetch once per session */
let cachedMentionables: Mentionable[] | null = null;

async function loadMentionables(): Promise<Mentionable[]> {
  if (cachedMentionables) return cachedMentionables;

  const results: Mentionable[] = [];

  try {
    const usersRes = await axios.post("/api/direct/query", {
      query:
        "SELECT id, first_name, last_name, user_name, email1 FROM Users WHERE status = 'Active' LIMIT 50;",
    });
    if (usersRes.data.success) {
      for (const u of usersRes.data.result || []) {
        const name =
          `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.user_name;
        if (name)
          results.push({ id: u.id, name, type: "user", email: u.email1 });
      }
    }
  } catch {
    /* */
  }

  try {
    const contactsRes = await axios.post("/api/direct/query", {
      query:
        "SELECT id, firstname, lastname, email FROM Contacts WHERE portal = '1' LIMIT 100;",
    });
    if (contactsRes.data.success) {
      for (const c of contactsRes.data.result || []) {
        const name = `${c.firstname || ""} ${c.lastname || ""}`.trim();
        if (name)
          results.push({ id: c.id, name, type: "contact", email: c.email });
      }
    }
  } catch {
    /* */
  }

  cachedMentionables = results;
  return results;
}

export const MentionTextarea: React.FC<MentionTextareaProps> = ({
  value,
  onChange,
  placeholder,
  rows = 3,
  onSubmit,
  disabled,
}) => {
  const {
    token: { colorBgContainer, colorBorder, colorBgTextHover, colorText, colorTextSecondary, borderRadiusLG, fontSize, fontSizeSM },
  } = theme.useToken();

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [mentionables, setMentionables] = useState<Mentionable[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [filter, setFilter] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState(-1);

  useEffect(() => {
    loadMentionables().then(setMentionables);
  }, []);

  const filtered = filter
    ? mentionables.filter(
        (m) =>
          m.name.toLowerCase().includes(filter.toLowerCase()) ||
          (m.email || "").toLowerCase().includes(filter.toLowerCase())
      )
    : mentionables;

  const insertMention = useCallback(
    (item: Mentionable) => {
      const textarea = textareaRef.current;
      if (!textarea || mentionStart < 0) return;

      const before = value.slice(0, mentionStart);
      const after = value.slice(textarea.selectionStart);
      const mention = `@[${item.name}](${item.id}) `;
      const newValue = before + mention + after;

      onChange(newValue);
      setShowDropdown(false);
      setFilter("");
      setMentionStart(-1);

      requestAnimationFrame(() => {
        const pos = before.length + mention.length;
        textarea.focus();
        textarea.setSelectionRange(pos, pos);
      });
    },
    [value, onChange, mentionStart]
  );

  const handleInput = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      onChange(newValue);

      const pos = e.target.selectionStart;
      const textBefore = newValue.slice(0, pos);
      const atIndex = textBefore.lastIndexOf("@");

      if (atIndex >= 0) {
        const charBefore = atIndex > 0 ? textBefore[atIndex - 1] : " ";
        if (charBefore === " " || charBefore === "\n" || atIndex === 0) {
          const query = textBefore.slice(atIndex + 1);
          if (!query.includes(" ") && query.length <= 30) {
            setMentionStart(atIndex);
            setFilter(query);
            setShowDropdown(true);
            setSelectedIndex(0);
            return;
          }
        }
      }

      setShowDropdown(false);
      setFilter("");
      setMentionStart(-1);
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (showDropdown && filtered.length > 0) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSelectedIndex((i) => (i + 1) % filtered.length);
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelectedIndex((i) => (i - 1 + filtered.length) % filtered.length);
          return;
        }
        if (e.key === "Enter" || e.key === "Tab") {
          e.preventDefault();
          insertMention(filtered[selectedIndex]);
          return;
        }
        if (e.key === "Escape") {
          setShowDropdown(false);
          return;
        }
      }

      // Cmd/Ctrl+Enter to submit
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && onSubmit) {
        e.preventDefault();
        onSubmit();
      }
    },
    [showDropdown, filtered, selectedIndex, insertMention, onSubmit]
  );

  // Scroll selected item into view
  useEffect(() => {
    if (!dropdownRef.current) return;
    const items = dropdownRef.current.children;
    if (items[selectedIndex]) {
      (items[selectedIndex] as HTMLElement).scrollIntoView({
        block: "nearest",
      });
    }
  }, [selectedIndex]);

  return (
    <div style={{ position: "relative" }}>
      <Input.TextArea
        ref={textareaRef as any}
        value={value}
        onChange={handleInput as any}
        onKeyDown={handleKeyDown as any}
        placeholder={placeholder ?? "Scrivi un commento, @menziona qualcuno..."}
        rows={rows}
        disabled={disabled}
        onBlur={() => {
          setTimeout(() => setShowDropdown(false), 200);
        }}
        style={{ resize: "none" }}
      />

      {showDropdown && filtered.length > 0 && (
        <div
          ref={dropdownRef}
          style={{
            position: "absolute",
            bottom: "100%",
            left: 0,
            right: 0,
            maxHeight: 220,
            overflowY: "auto",
            background: colorBgContainer,
            border: `1px solid ${colorBorder}`,
            borderRadius: borderRadiusLG,
            boxShadow: "0 -4px 12px rgba(0,0,0,0.08)",
            zIndex: 1000,
          }}
        >
          {filtered.map((item, idx) => (
            <div
              key={item.id}
              onMouseDown={(e) => {
                e.preventDefault();
                insertMention(item);
              }}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background:
                  idx === selectedIndex ? colorBgTextHover : "transparent",
                borderBottom:
                  idx < filtered.length - 1
                    ? `1px solid ${colorBorder}`
                    : "none",
                transition: "background 0.15s ease",
              }}
            >
              <span>
                <span
                  style={{
                    fontWeight: 500,
                    fontSize,
                    color: colorText,
                  }}
                >
                  {item.name}
                </span>
                {item.email && (
                  <span
                    style={{
                      fontSize: fontSizeSM,
                      color: colorTextSecondary,
                      marginLeft: 8,
                    }}
                  >
                    {item.email}
                  </span>
                )}
              </span>
              <Tag
                color={item.type === "user" ? "blue" : "green"}
                style={{ margin: 0, fontSize: fontSizeSM - 1 }}
              >
                {item.type === "user" ? "STAFF" : "CONTATTO"}
              </Tag>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
